// FLAGS: identity
// First-time authenticator setup: renders the server-generated QR and marks the authenticator enabled.
import { useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@openup/shared/primitives/ui/button";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import {
  useEnableAuthenticatorMutation,
  useTwoFactorRegistrationInfo,
} from "@src/queries/auth";

interface EnrollAuthenticatorStepProps {
  /** Called when enrollment is confirmed (or was already done) — proceed to code entry. */
  onEnrolled: () => void;
}

/**
 * FLAGS: identity
 * The rendered QR is the account's TOTP shared key, so this component must stay the
 * only thing that holds it — no logging, no persistence. Where the API reports an
 * authenticator already exists, the step skips itself rather than issuing a second key.
 * First-time authenticator (TOTP) enrollment, mirroring the
 * customer-success-dashboard: show the server-generated otpauth QR, the user
 * scans it with an authenticator app, confirms, then verifies the first code
 * in the TOTP step.
 */
const EnrollAuthenticatorStep = ({
  onEnrolled,
}: EnrollAuthenticatorStepProps) => {
  const {
    data: qrCodeUrl,
    isPending,
    isError,
  } = useTwoFactorRegistrationInfo();
  const enableMutation = useEnableAuthenticatorMutation();

  // API answers `false` (→ null) when an authenticator is already enrolled —
  // nothing to set up, go straight to code entry.
  useEffect(() => {
    if (!isPending && !isError && qrCodeUrl === null) {
      onEnrolled();
    }
  }, [isPending, isError, qrCodeUrl, onEnrolled]);

  const onConfirmScanned = () => {
    enableMutation.mutate(undefined, {
      onSuccess: onEnrolled,
      onError: () =>
        toast.error("Could not enable the authenticator. Please try again."),
    });
  };

  if (isPending) {
    return (
      <div
        className="flex justify-center py-8"
        role="status"
        aria-label="Loading"
      >
        <Spinner />
      </div>
    );
  }

  if (isError || !qrCodeUrl) {
    return (
      <p className="text-center text-gray-600" role="alert">
        Could not load the authenticator setup. Refresh the page to try again.
      </p>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <p className="text-gray-600">
        Your account needs an authenticator app. Scan this QR code with Google
        Authenticator (or any TOTP app), then continue.
      </p>
      <img src={qrCodeUrl} alt="Authenticator QR code" className="h-44 w-44" />
      <Button
        variant="default"
        size="default"
        onClick={onConfirmScanned}
        disabled={enableMutation.isPending}
      >
        {enableMutation.isPending ? "Enabling…" : "I've scanned the code"}
      </Button>
    </div>
  );
};

export default EnrollAuthenticatorStep;
